/**
 * React hooks reading the editor store. Writes go through the edit session
 * (`useEditSession` in `@/platform/editor-sync-context`), never from here.
 * @module hooks/useStore
 */

import { useCallback } from "react";

import { useEditorStore } from "@/state/store";
import type { Programme } from "@/types";

/**
 * Hook to access the current programme state. Every write gives the programme
 * a new identity, so it serves as a memo dependency as it is.
 */
export function useProgramme(): { programme: Programme } {
  return { programme: useEditorStore((store) => store.programme) };
}

/**
 * Hook to get the current step index.
 */
export function useStepIndex(): number {
  return useEditorStore((store) => store.stepIndex);
}

/**
 * Hook to trigger the global render function.
 * Useful when React components need to trigger a full legacy re-render.
 *
 * @returns A function to trigger global render
 */
export function useGlobalRender(): () => void {
  return useCallback(() => {
    const win = window as Window & { render?: () => void | Promise<void> };
    win.render?.();
  }, []);
}
